import { createTheme, type PaletteOptions, type Theme } from '@mui/material/styles';

import type { ResolvedTheme } from '@/modules/theme';

// The page background: the palette's secondary.main, the theme-color meta and the pre-paint <style> in index.html,
// which repeats these values by hand and must match them or the page changes shade when the app mounts
export const PAGE_BACKGROUND: Readonly<Record<ResolvedTheme, string>> = {
    light: '#f9f9f9',
    dark: '#121212',
};

const PALETTES: Readonly<Record<ResolvedTheme, PaletteOptions>> = {
    light: {
        mode: 'light',
        primary: {
            main: '#00D55B',
            contrastText: '#000000',
        },
        secondary: {
            main: PAGE_BACKGROUND.light,
            light: '#ffffff',
        },
        text: {
            primary: '#000000',
            secondary: '#666666',
        },
        error: {
            main: '#aa0000',
        },
        // The standard Alert takes its text and background from `light` (MUI v4 took them from `main`)
        success: {
            main: '#00D55B',
            light: '#00D55B',
        },
        warning: {
            main: '#ff9800',
            light: '#ff9800',
        },
        action: {
            active: '#000000',
        },
        background: {
            paper: '#ffffff',
        },
    },
    // The light palette mirrored: the page is the darkest surface and the paper card one step lighter
    dark: {
        mode: 'dark',
        primary: {
            main: '#00D55B',
            contrastText: '#000000',
        },
        secondary: {
            main: PAGE_BACKGROUND.dark,
            light: '#1e1e1e',
        },
        text: {
            primary: '#ffffff',
            secondary: '#a0a0a0',
        },
        // #aa0000 does not read on a dark background
        error: {
            main: '#ef5350',
        },
        success: {
            main: '#00D55B',
            light: '#00D55B',
        },
        warning: {
            main: '#ff9800',
            light: '#ff9800',
        },
        action: {
            active: '#ffffff',
        },
        background: {
            default: PAGE_BACKGROUND.dark,
            paper: '#1e1e1e',
        },
    },
};

export const createAppTheme = (mode: ResolvedTheme): Theme => createTheme({
    palette: PALETTES[mode],
    breakpoints: {
        values: {
            xs: 0,
            sm: 576,
            md: 768,
            lg: 992,
            xl: 1200,
        },
    },
});
